import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Toast from '../components/Toast';
import VerifiedBadge from '../components/VerifiedBadge';
import { formatPts } from '../lib/format';
import ReminderBell from '../components/ReminderBell';
import { BadgeStrip } from '../components/BadgeStrip';
import api, { resolveAvatarUrl } from '../api';
import SocialsDisplay from '../components/SocialsDisplay';
import { isSqrtClan, fmtLaunchedAgo, fmtBits } from '../lib/clan';

/**
 * 04 — Creator Profile (Step 3 + P1 real-data enhancements).
 * Ported from /app/reference/04-creator-profile.html.
 *
 * REAL (from backend):
 *   - Identity, gradient banner, bio, category
 *   - Bit price, 7d change, market cap, Holders count, Min. to join
 *   - Bonding-curve mini viz
 *   - Live sparkline chart from trades (falls back to synthetic if no history)
 *   - Clan Members tab — real holders sorted by bits_owned
 *   - Activity tab — real recent trades
 *   - Buy/Sell → /trade/:handle (fully functional in Step 4)
 */

const CATEGORY_COLOR = {
  'Trader': '#FF5722', 'Content Creator': '#E040FB', 'Coach': '#00B4FF',
  'Educator': '#B4FF00', 'Community Builder': '#00B4FF', 'Influencer': '#FFC107',
};

const MEMBER_TINTS = ['#00B4FF', '#E040FB', '#B4FF00', '#FF5722', '#00B4FF', '#FFC107'];

const DEFAULT_PERKS = [
  { label: '🎙️ Weekly live Q&A', color: '#FF5722' },
  { label: '📊 Exclusive breakdowns', color: '#E040FB' },
  { label: '💬 Priority DM replies', color: '#00B4FF' },
  { label: '🎧 Members-only voice chats', color: '#00B4FF' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Phase 1 — microsecond arrival timestamp. Renders as HH:MM:SS.µµµ so power
// users (and eventually the launch-replay tool) can see exact trade ordering.
function fmtArrivedUs(us) {
  if (!us || typeof us !== 'number') return null;
  const ms = Math.floor(us / 1000);
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const micros = String(us % 1_000_000).padStart(6, '0');
  return `${hh}:${mm}:${ss}.${micros}`;
}

export default function CreatorProfile() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState('about');
  const [timeframe, setTimeframe] = useState('24H');
  const [toast, setToast] = useState(null);

  const [members, setMembers] = useState(null);        // null → loading
  const [activity, setActivity] = useState(null);      // null → loading
  const [chartPoints, setChartPoints] = useState(null);
  const [avatarOk, setAvatarOk] = useState(true); // null → loading
  const [access, setAccess] = useState(null);          // {is_creator, is_member, bits_owned, min_bits_to_join}
  const [ownerStats, setOwnerStats] = useState(null);   // /clans/mine/stats — only fetched when viewer IS the creator
  const [bookmarked, setBookmarked] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  // First-visit banner promoting the public Audience Guide. Suppressed
  // permanently after dismissal via localStorage. Only shown to fans
  // who don't yet hold Bits in this Clan.
  const [showGuideBanner, setShowGuideBanner] = useState(() => {
    try { return localStorage.getItem('clanster_hide_audience_guide_banner') !== '1'; }
    catch (_) { return true; }
  });
  const dismissGuideBanner = () => {
    try { localStorage.setItem('clanster_hide_audience_guide_banner', '1'); } catch (_) {}
    setShowGuideBanner(false);
  };

  useEffect(() => {
    let cancelled = false;
    setCreator(null); setNotFound(false);
    setMembers(null); setActivity(null); setChartPoints(null);
    setAccess(null); setBookmarked(false);
    api.get(`/creators/${handle}`)
      .then(({ data }) => { if (!cancelled) setCreator(data); })
      .catch((e) => { if (!cancelled) setNotFound(e.response?.status === 404); });
    // Fetch tab data in parallel (cheap, keeps switching snappy)
    api.get(`/creators/${handle}/members`).then(({ data }) => { if (!cancelled) setMembers(data.members || []); }).catch(() => { if (!cancelled) setMembers([]); });
    api.get(`/creators/${handle}/activity`).then(({ data }) => { if (!cancelled) setActivity(data.trades || []); }).catch(() => { if (!cancelled) setActivity([]); });
    api.get(`/clans/${handle}/access`).then(({ data }) => {
      if (cancelled) return;
      setAccess(data);
      // If the viewer IS the creator of this Clan, fetch their earnings
      // stats so the Ongoing royalties card can show real numbers instead
      // of just the minted-count. `/clans/mine/stats` is auth-gated to the
      // creator, so a fan viewing the same page never sees these figures.
      if (data && data.is_creator) {
        api.get('/clans/mine/stats').then(({ data: s }) => {
          if (!cancelled) setOwnerStats(s);
        }).catch(() => {});
      }
    }).catch(() => { if (!cancelled) setAccess(null); });
    api.get(`/bookmarks/${handle}`).then(({ data }) => { if (!cancelled) setBookmarked(!!data.bookmarked); }).catch(() => {});
    return () => { cancelled = true; };
  }, [handle]);

  // Reset avatar-loaded flag when navigating between creators.
  useEffect(() => { setAvatarOk(true); }, [handle]);

  // Refetch chart when the timeframe changes.
  useEffect(() => {
    if (!handle) return;
    let cancelled = false;
    const days = timeframe === '24H' ? 1 : timeframe === '7D' ? 7 : timeframe === '1M' ? 30 : 3650; // "All" = 10y
    setChartPoints(null);
    api.get(`/creators/${handle}/chart`, { params: { days } })
      .then(({ data }) => { if (!cancelled) setChartPoints(data.points || []); })
      .catch(() => { if (!cancelled) setChartPoints([]); });
    return () => { cancelled = true; };
  }, [handle, timeframe]);

  // ---- Phase 7 · Change 9d + 9e wiring ----
  const onShare = async () => {
    // Use the backend share URL so WhatsApp/Telegram/X get a per-Clan OG
    // preview (avatar + bit price). Humans get meta-refreshed to the SPA.
    const url = `${window.location.origin}/api/share/creator/${handle}`;
    const title = `Check out ${creator?.display_name || '@' + handle} on Clanster`;
    if (navigator.share) {
      try {
        await navigator.share({ title, url, text: title });
        return;
      } catch {
        // user cancelled or failed — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setToast({ msg: '✓ Link copied to clipboard' });
    } catch {
      setToast({ msg: url });
    }
  };

  const onToggleBookmark = async () => {
    try {
      if (bookmarked) {
        await api.delete(`/bookmarks/${handle}`);
        setBookmarked(false);
        setToast({ msg: '🔖 Removed from bookmarks' });
      } else {
        await api.post('/bookmarks', { creator_handle: handle });
        setBookmarked(true);
        setToast({ msg: '🔖 Saved to bookmarks' });
      }
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Could not update bookmark.' });
    }
  };

  const onJoinClan = () => {
    // Smart routing per Phase 7 · Change 9d.
    // If user is already a member, go to the Clan feed. Otherwise show a
    // compact modal explaining they need N more Bits.
    if (!access) return;
    if (access.is_member || access.is_creator) {
      navigate(`/clan/${handle}/feed`, { state: { from: '/creator' } });
    } else {
      setShowJoinModal(true);
    }
  };

  // Chart: prefer real trade history; fall back to synthetic sparkline if none yet.
  const spark = useMemo(() => {
    if (!creator) return null;
    if (chartPoints && chartPoints.length >= 2) {
      return chartPoints.map((p) => p.price);
    }
    // Synthetic fallback (Step-3 look)
    const end = creator.clan.current_price;
    const change = (creator.clan.change_24h || 0) / 100;
    const start = end / (1 + change);
    const N = 12;
    const pts = [];
    let seed = handle.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    for (let i = 0; i < N; i++) {
      const t = i / (N - 1);
      const base = start + (end - start) * t;
      const noise = (rand() - 0.5) * (end - start) * 0.35;
      pts.push(Math.max(0.001, base + noise));
    }
    pts[N - 1] = end;
    return pts;
  }, [creator, handle, chartPoints]);

  if (notFound) {
    return (
      <AppShell activeTab="home">
        <div className="cp-wrap">
          <div className="cp-notfound">
            <h1>Creator not found</h1>
            <p>We couldn't find <b>@{handle}</b> on Clanster.</p>
            <Link to="/home" className="btn-secondary" data-testid="cp-back-home">Back to Home</Link>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!creator) {
    return (
      <AppShell activeTab="home">
        <div className="cp-wrap"><div className="loading-shell">loading creator…</div></div>
      </AppShell>
    );
  }

  const c = creator;
  const primaryCategory = c.categories?.[0] || 'Creator';
  const tagColor = CATEGORY_COLOR[primaryCategory] || '#00B4FF';
  const change24 = Number(c.clan.change_24h || 0);
  const up = change24 >= 0;
  const priceDelta = c.clan.current_price - c.clan.current_price / (1 + change24 / 100);
  const holders = c.clan.members_count || 0;
  const hasRealChart = chartPoints && chartPoints.length >= 2;

  // Build the SVG path for the sparkline
  const sparkPath = (() => {
    if (!spark) return { line: '', area: '' };
    const min = Math.min(...spark);
    const max = Math.max(...spark);
    const rng = max - min || 1;
    const W = 400, H = 110, pad = 8;
    const coords = spark.map((v, i) => {
      const x = (i / (spark.length - 1)) * W;
      const y = H - pad - ((v - min) / rng) * (H - pad * 2);
      return [x, y];
    });
    const line = 'M' + coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' L');
    const area = `${line} L${W},${H} L0,${H} Z`;
    return { line, area };
  })();

  return (
    <AppShell activeTab="home">
      <div className="cp-wrap" data-testid="creator-profile">
        {/* Top action bar */}
        <div className="cp-top-bar">
          <button className="cp-icon-btn" aria-label="Back" onClick={() => navigate('/home')} data-testid="cp-back-btn">
            <svg viewBox="0 0 24 24" fill="none" width="17" height="17">
              <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cp-icon-btn" aria-label="Share" onClick={onShare} data-testid="cp-share-btn">
              <svg viewBox="0 0 24 24" fill="none" width="17" height="17">
                <circle cx="18" cy="5" r="2.4" stroke="currentColor" strokeWidth="1.6" />
                <circle cx="6" cy="12" r="2.4" stroke="currentColor" strokeWidth="1.6" />
                <circle cx="18" cy="19" r="2.4" stroke="currentColor" strokeWidth="1.6" />
                <path d="M8.2 10.8l7.6-4.4M8.2 13.2l7.6 4.4" stroke="currentColor" strokeWidth="1.6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="cp-grid">
          {/* Banner — creator's uploaded cover if present, else auto gradient */}
          <div
            className="cp-banner"
            style={c.cover_url
              ? { backgroundImage: `url(${c.cover_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : { background: `linear-gradient(120deg, ${c.gradient[0]}, ${c.gradient[1]})` }}
          >
            <div
              className="cp-avatar-xl"
              style={{ background: `linear-gradient(135deg, ${c.gradient[0]}, ${c.gradient[1]})` }}
              data-testid="cp-avatar"
            >
              {(() => {
                const resolvedAvatar = resolveAvatarUrl(c.avatar);
                if (resolvedAvatar && avatarOk) {
                  return (
                    <img
                      src={resolvedAvatar}
                      alt={c.display_name || c.handle}
                      className="cp-avatar-xl-img"
                      onError={() => setAvatarOk(false)}
                    />
                  );
                }
                return c.initials;
              })()}
            </div>
          </div>

          {/* SIDEBAR */}
          <aside className="cp-sidebar">
            <div className="cp-identity">
              <div className="cp-name-line">
                <span className="cp-name" data-testid="cp-name">{c.display_name}</span>
                {c.verified && <VerifiedBadge size="md" />}
                <span className="cp-tag" style={{ background: tagColor }}>{primaryCategory}</span>
                {c.clan.sold_out && !isSqrtClan(c.clan) && (
                  <span className="sold-out-badge" data-testid="cp-sold-out-badge">🔒 Sold Out</span>
                )}
              </div>
              <div className="cp-handle" data-testid="cp-handle">@{c.handle}</div>
              {isSqrtClan(c.clan) ? (
                <div style={{ marginTop: 8 }} data-testid="cp-supply-progress">
                  <div className="supply-progress-label">
                    <b>{fmtBits(c.clan.bits_minted)}</b> Bit{c.clan.bits_minted === 1 ? '' : 's'} minted
                    {fmtLaunchedAgo(c.clan.launch_at) && <span> · {fmtLaunchedAgo(c.clan.launch_at)}</span>}
                  </div>
                </div>
              ) : c.clan.supply_cap && c.clan.supply_cap < 1_000_000 && (
                <div style={{ marginTop: 8 }} data-testid="cp-supply-progress">
                  <div className="supply-progress-label">
                    <b>{c.clan.bits_minted.toLocaleString()}</b> / {c.clan.supply_cap.toLocaleString()} Bits minted
                    ({Math.min(100, ((c.clan.bits_minted / c.clan.supply_cap) * 100)).toFixed(1)}%)
                  </div>
                  <div className="supply-progress">
                    <div
                      className="supply-progress-fill"
                      style={{ width: `${Math.min(100, (c.clan.bits_minted / c.clan.supply_cap) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <p className="cp-bio" data-testid="cp-bio">{c.bio || '—'}</p>

            {/* Trader badges — permanent achievements this user has earned across
                all their trading activity. Renders nothing if the user has zero
                badges (see BadgeStrip.jsx). */}
            <BadgeStrip handle={c.handle} />

            <div className="cp-action-row">
              <button className="btn-join" onClick={onJoinClan} data-testid="cp-join-btn">
                {access?.is_member || access?.is_creator ? '→ Enter Clan' : '+ Join Clan'}
              </button>
              {/* Watch Live / Trade Live — jumps straight to the trade page for
                  this Clan. Only shown post-launch (pre-launch has no trail
                  yet, so the button would be misleading). Label pivots on
                  ownership: the creator of THIS clan sees "Watch Live"
                  (they can't trade their own Bits — passive spectator view
                  of their royalties ticking up). Everyone else — fans and
                  OTHER creators viewing this clan — sees "Trade Live"
                  because for them the destination is an actionable buy/sell
                  surface, not a spectator screen. */}
              {(!c.clan?.launch_at || new Date(c.clan.launch_at).getTime() <= Date.now()) && (
                <button
                  type="button"
                  className="btn-watch-live"
                  onClick={() => navigate(`/trade?clan=${c.handle}`)}
                  data-testid="cp-watch-live-btn"
                  title={access?.is_creator ? "Watch this Clan's live trades on the RocketChart" : "Trade this Clan's Bits on the live chart"}
                >
                  <span className="btn-watch-live-icon" aria-hidden="true">⚡</span>
                  <span className="btn-watch-live-txt">{access?.is_creator ? 'Watch Live' : 'Trade Live'}</span>
                </button>
              )}
              <button
                className="cp-icon-btn"
                aria-label="Message"
                onClick={() => navigate(`/messages/${c.handle}`)}
                data-testid="cp-msg-btn"
              >
                <svg viewBox="0 0 24 24" fill="none" width="17" height="17">
                  <path d="M4 5h16v11H8l-4 4V5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                className="cp-icon-btn"
                aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark'}
                aria-pressed={bookmarked}
                onClick={onToggleBookmark}
                data-testid="cp-bookmark-btn"
                style={bookmarked ? { background: 'rgba(0, 180, 255, 0.14)' } : undefined}
              >
                <svg viewBox="0 0 24 24" fill={bookmarked ? '#00B4FF' : 'none'} width="17" height="17">
                  <path d="M6 3h12v18l-6-4-6 4V3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                </svg>
              </button>
              {/* Reminder bell — visible only when this Clan is pre-launch
                  (scheduled but hasn't opened yet). Mirrors the bell in the
                  RocketChart pre-launch overlay so fans can subscribe from
                  either surface without having to open the Trade sheet. */}
              {c.clan?.launch_at && new Date(c.clan.launch_at).getTime() > Date.now() && (
                <ReminderBell
                  handle={c.handle}
                  variant="icon"
                  className="cp-icon-btn cp-reminder-bell"
                />
              )}
            </div>

            {/* Owner action strip — visible ONLY to the creator when they
                view their own profile. Puts the two highest-leverage growth
                actions (Get Verified / Get Featured) and the Manage Clan
                entry one tap away instead of buried behind Enter Clan → gear
                → scroll. Hidden from fans entirely. */}
            {access?.is_creator && (
              <div className="cp-owner-strip" data-testid="cp-owner-strip">
                <div className="cp-owner-strip-lbl">Creator tools</div>
                <div className="cp-owner-strip-actions">
                  {!c.verified && (
                    <button
                      type="button"
                      className="cp-owner-btn cp-owner-verify"
                      onClick={() => navigate('/verify')}
                      data-testid="cp-owner-verify"
                      title="Apply for the verified badge — increases fan trust and unlocks Featured slots"
                    >
                      <span className="cp-owner-btn-icon" aria-hidden="true">✓</span>
                      <span className="cp-owner-btn-txt">Get Verified</span>
                    </button>
                  )}
                  {c.verified && (
                    <span className="cp-owner-btn cp-owner-verified" data-testid="cp-owner-verified">
                      <span className="cp-owner-btn-icon" aria-hidden="true">✓</span>
                      <span className="cp-owner-btn-txt">Verified</span>
                    </span>
                  )}
                  <button
                    type="button"
                    className="cp-owner-btn cp-owner-featured"
                    onClick={() => navigate('/featured/buy')}
                    data-testid="cp-owner-featured"
                    title="Buy a Featured slot — pin your Clan on the discovery grid for 24-72h"
                  >
                    <span className="cp-owner-btn-icon" aria-hidden="true">⭐</span>
                    <span className="cp-owner-btn-txt">Get Featured</span>
                  </button>
                  <button
                    type="button"
                    className="cp-owner-btn cp-owner-manage"
                    onClick={() => navigate('/manage-clan')}
                    data-testid="cp-owner-manage"
                    title="Manage Clan — settings, perks, danger zone"
                  >
                    <span className="cp-owner-btn-icon" aria-hidden="true">⚙</span>
                    <span className="cp-owner-btn-txt">Manage Clan</span>
                  </button>
                </div>
              </div>
            )}

            {showGuideBanner && !access?.is_member && !access?.is_creator && (
              <div className="cp-guide-hint" data-testid="cp-audience-guide-banner">
                <span className="cp-guide-hint-icon" aria-hidden="true">💡</span>
                <span className="cp-guide-hint-text">
                  New to trading Bits?{' '}
                  <a
                    href="/guides/audience-prep"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cp-guide-hint-link"
                    data-testid="cp-audience-guide-read"
                  >
                    Read the Fan Trading Playbook
                  </a>
                </span>
                <button
                  type="button"
                  className="cp-guide-hint-close"
                  onClick={dismissGuideBanner}
                  aria-label="Dismiss"
                  data-testid="cp-audience-guide-dismiss"
                >
                  ×
                </button>
              </div>
            )}

            <div className="cp-stats-grid">
              <div className="cp-stat" data-testid="cp-stat-price">
                <div className="cp-stat-label">Bit price</div>
                <div className="cp-stat-value">{c.clan.current_price.toFixed(2)} pts</div>
                <div className={`cp-stat-change ${up ? 'up' : 'down'}`}>
                  <svg viewBox="0 0 12 12" fill="none">
                    {up
                      ? <path d="M2 8l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      : <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
                  </svg>
                  {Math.abs(change24).toFixed(1)}% · 24h
                </div>
              </div>
              <div className="cp-stat" data-testid="cp-stat-mcap">
                <div className="cp-stat-label">Market cap</div>
                <div className="cp-stat-value">
                  {c.clan.market_cap >= 1000
                    ? `${formatPts(c.clan.market_cap / 1000, 2)}K pts`
                    : `${formatPts(c.clan.market_cap, 2)} pts`}
                </div>
              </div>
              <div className="cp-stat" data-testid="cp-stat-holders">
                <div className="cp-stat-label">Holders</div>
                <div className="cp-stat-value">{holders.toLocaleString()}</div>
                <div className="cp-stat-hint">holding ≥ {c.clan.min_bits_to_join} Bit{c.clan.min_bits_to_join === 1 ? '' : 's'}</div>
              </div>
              <div className="cp-stat" data-testid="cp-stat-min">
                <div className="cp-stat-label">Min. to join clan</div>
                <div className="cp-stat-value">{c.clan.min_bits_to_join} Bit{c.clan.min_bits_to_join === 1 ? '' : 's'}</div>
              </div>
            </div>

            {access?.is_creator ? (
              <div className="cp-cbit-card" data-testid="cp-creators-bit-card">
                {isSqrtClan(c.clan) ? (
                  // sqrt clans have no "sold out" moment and no lump-sum
                  // Creator's Bit — the reward model is 100% ongoing 5%
                  // royalties on every buy AND every sell, forever. Show
                  // that + a compact minted-count summary.
                  <>
                    <div className="cp-cbit-head">
                      <span className="cp-cbit-emoji">💎</span>
                      <div className="cp-cbit-title">
                        Ongoing royalties
                        <span className="cp-cbit-sub">5% of every trade — paid to your wallet forever</span>
                      </div>
                    </div>
                    <div className="cp-cbit-payout" data-testid="cp-cbit-payout">
                      {fmtBits(c.clan.bits_minted)} Bit{c.clan.bits_minted === 1 ? '' : 's'} minted
                    </div>
                    {/* Actual earnings for the creator — pulled from
                        /clans/mine/stats when the viewer is the owner.
                        Splits royalties + perks so the number matches what
                        they'll also see on Manage Clan. */}
                    {ownerStats && (
                      <div className="cp-cbit-earned" data-testid="cp-cbit-earned">
                        <span className="cp-cbit-earned-lbl">Earned so far</span>
                        <span className="cp-cbit-earned-val">
                          {(
                            (ownerStats.royalties_earned || 0)
                            + (ownerStats.perks_revenue || 0)
                          ).toLocaleString(undefined, { maximumFractionDigits: 2 })} pts
                        </span>
                      </div>
                    )}
                    <div className="cp-cbit-caption">
                      That's your take so far — keep growing and this number climbs every time a new fan finds you and trades.
                    </div>
                  </>
                ) : c.clan.founder_paid ? (
                  <>
                    <div className="cp-cbit-head">
                      <span className="cp-cbit-emoji">🎉</span>
                      <div className="cp-cbit-title">
                        Creator's Bit · Cashed
                        <span className="cp-cbit-sub">
                          +{Math.round(c.clan.founder_paid_amount).toLocaleString()} pts
                          {c.clan.founder_paid_at ? ` · ${new Date(c.clan.founder_paid_at).toLocaleDateString(undefined, {month:'short', day:'numeric', year:'numeric'})}` : ''}
                        </span>
                      </div>
                    </div>
                    <div className="cp-cbit-caption">
                      Your Clan is fully minted — every Bit belongs to a community member.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="cp-cbit-head">
                      <span className="cp-cbit-emoji">💎</span>
                      <div className="cp-cbit-title">
                        Creator's Bit
                        <span className="cp-cbit-sub">Your payout when the Clan sells out</span>
                      </div>
                    </div>
                    <div className="cp-cbit-payout" data-testid="cp-cbit-payout">
                      {c.clan.lifetime_value
                        ? `${c.clan.lifetime_value.toLocaleString()} pts`
                        : '—'}
                    </div>
                    <div className="cp-cbit-progress">
                      <div className="cp-cbit-bar">
                        <div
                          className="cp-cbit-fill"
                          style={{
                            width: `${Math.min(100, (c.clan.bits_minted / (c.clan.supply_cap || 1)) * 100).toFixed(2)}%`,
                          }}
                        />
                      </div>
                      <div className="cp-cbit-progress-text">
                        {c.clan.bits_minted.toLocaleString()} / {c.clan.supply_cap.toLocaleString()} Bits sold
                        <span> · {((c.clan.bits_minted / (c.clan.supply_cap || 1)) * 100).toFixed(1)}% filled</span>
                      </div>
                    </div>
                    <div className="cp-cbit-caption">
                      Drops into your Wallet automatically the moment your Clan hits 100%. You're also earning a 5% royalty on every trade along the way.
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="cp-trade-bar">
                <button className="cp-btn-sell" onClick={() => navigate(`/trade/${c.handle}`, { state: { mode: 'sell' } })} data-testid="cp-sell-btn">
                  Sell
                </button>
                <button className="cp-btn-buy" onClick={() => navigate(`/trade/${c.handle}`, { state: { mode: 'buy' } })} data-testid="cp-buy-btn">
                  Buy Bit
                </button>
              </div>
            )}
            {/* The old "Enter Clan Feed" secondary button was removed
                Feb 2026 — the primary "→ Enter Clan" pill in the top
                action row already covers this exact intent for members,
                and having two entry points on the same page was
                duplicative. If the user isn't a member yet, the Buy Bit
                CTA above is what unlocks feed access; once bought,
                they'll bounce back and see the top-row Enter Clan pill. */}
          </aside>

          {/* MAIN */}
          <main className="cp-main">
            {/* Chart */}
            <div className="cp-chart-card" data-testid="cp-chart">
              <div className="cp-chart-head">
                <div>
                  <div className="cp-chart-price">{c.clan.current_price.toFixed(2)} pts</div>
                  <div className={`cp-chart-change ${up ? 'up' : 'down'}`}>
                    <svg viewBox="0 0 12 12" fill="none">
                      {up
                        ? <path d="M2 8l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        : <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
                    </svg>
                    {up ? '+' : '−'}{Math.abs(priceDelta).toFixed(2)} pts ({Math.abs(change24).toFixed(1)}%) past 24 hours
                  </div>
                </div>
                <div className="cp-tf-row">
                  {['24H', '7D', '1M', 'All'].map((t) => (
                    <button
                      key={t}
                      className={`cp-tf-btn${timeframe === t ? ' active' : ''}`}
                      onClick={() => setTimeframe(t)}
                      data-testid={`cp-tf-${t}`}
                    >{t}</button>
                  ))}
                </div>
              </div>
              <BubbleChart points={chartPoints} up={up} data-testid="cp-bubble-chart" />
              <p className="cp-chart-note" data-testid="cp-chart-note">
                {hasRealChart
                  ? `Bubble = single trade · size = Bits traded · 🟢 buy · 🔴 sell — ${chartPoints.length} trade${chartPoints.length === 1 ? '' : 's'} ${timeframe === 'All' ? 'all time' : `in the last ${timeframe === '24H' ? '24 hours' : timeframe === '7D' ? '7 days' : '30 days'}`}.`
                  : chartPoints === null
                    ? 'Loading trades…'
                    : 'No trades yet in this range — try a longer timeframe.'}
              </p>
            </div>

            {/* Tabs */}
            <div className="cp-tabs-row" role="tablist">
              {[
                { key: 'about', label: 'About' },
                { key: 'members', label: `Clan Members${holders > 0 ? ` · ${holders}` : ''}` },
                { key: 'activity', label: `Activity${activity && activity.length ? ` · ${activity.length}` : ''}` },
              ].map((t) => (
                <button
                  key={t.key}
                  className={`cp-tab-btn${tab === t.key ? ' active' : ''}`}
                  onClick={() => setTab(t.key)}
                  role="tab"
                  aria-selected={tab === t.key}
                  data-testid={`cp-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'about' && (
              <div className="cp-tab-panel" data-testid="cp-panel-about">
                <SocialsDisplay socials={c.socials} isOwner={!!access?.is_creator} />
                <div className="cp-info-list">
                  <div className="cp-info-row">
                    <span className="label">Start price</span>
                    <span className="value" data-testid="cp-info-multiplier">{c.clan.multiplier.toFixed(2)} pts</span>
                  </div>
                  <div className="cp-info-row">
                    <span className="label">Bits minted (Volume)</span>
                    <span className="value" data-testid="cp-info-bits-minted">{c.clan.bits_minted.toLocaleString()}</span>
                  </div>
                  <div className="cp-info-row">
                    <span className="label">Creator royalty</span>
                    <span className="value">5% per trade</span>
                  </div>
                  <div className="cp-info-row">
                    <span className="label">Joined Clanster</span>
                    <span className="value">{c.joined_at}</span>
                  </div>
                </div>

                <div className="cp-curve-chip">
                  <div className="cp-curve-label">How {c.display_name.split(' ')[0]}'s Bit is priced</div>
                  <div className="cp-curve-row">
                    {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="cp-curve-bubble" />)}
                  </div>
                  <div className="cp-curve-caption">
                    <span>1st bit · {c.clan.multiplier.toFixed(2)} pts</span>
                    <span>100th bit · {(c.clan.multiplier * 100).toFixed(2)} pts</span>
                  </div>
                </div>

                <div className="cp-perks">
                  <div className="cp-perks-head">
                    <span className="cp-perks-title">What {c.display_name.split(' ')[0]}'s Clan gets you</span>
                    <span className="soon-pill">Default perks</span>
                  </div>
                  <div>
                    {DEFAULT_PERKS.map((p) => (
                      <span key={p.label} className="cp-perk-tag" style={{ background: p.color }}>{p.label}</span>
                    ))}
                  </div>
                  <p className="cp-perks-note">This creator hasn't customized their perks yet — these are the default rewards every Clan starts with.</p>
                </div>
              </div>
            )}

            {tab === 'members' && (
              <div className="cp-tab-panel" data-testid="cp-panel-members">
                {members === null && (
                  <div className="cp-empty-panel"><p>Loading members…</p></div>
                )}
                {members !== null && members.length === 0 && (
                  <div className="cp-empty-panel" data-testid="cp-members-empty">
                    <div className="cp-empty-icon">👥</div>
                    <h3>No one has crossed the join threshold yet.</h3>
                    <p>The first person to hold {c.clan.min_bits_to_join} Bit{c.clan.min_bits_to_join === 1 ? '' : 's'} will show up here.</p>
                  </div>
                )}
                {members !== null && members.length > 0 && (
                  <>
                    <div className="cp-members-summary">
                      <b>{members.length}</b> <span>member{members.length === 1 ? '' : 's'} holding ≥ {c.clan.min_bits_to_join} Bit{c.clan.min_bits_to_join === 1 ? '' : 's'}</span>
                    </div>
                    <div className="cp-members-list">
                      {members.map((m, idx) => (
                        <Link key={m.user_id} to={`/creator/${m.handle}`} className="cp-member-row" data-testid={`cp-member-${m.handle || idx}`}>
                          <div
                            className="cp-member-mini"
                            style={{ background: `linear-gradient(135deg, ${m.gradient?.[0] || MEMBER_TINTS[idx % MEMBER_TINTS.length]}, ${m.gradient?.[1] || MEMBER_TINTS[(idx + 2) % MEMBER_TINTS.length]})` }}
                          >
                            {m.initials}
                          </div>
                          <div className="cp-member-info">
                            <div className="cp-member-name">{m.display_name}</div>
                            {m.handle && <div className="cp-member-handle">@{m.handle}</div>}
                          </div>
                          <div className="cp-member-bits" data-testid={`cp-member-bits-${m.handle || idx}`}>
                            {m.bits_owned} Bit{m.bits_owned === 1 ? '' : 's'}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === 'activity' && (
              <div className="cp-tab-panel" data-testid="cp-panel-activity">
                {activity === null && (
                  <div className="cp-empty-panel"><p>Loading activity…</p></div>
                )}
                {activity !== null && activity.length === 0 && (
                  <div className="cp-empty-panel" data-testid="cp-activity-empty">
                    <div className="cp-empty-icon">📈</div>
                    <h3>No trades yet on {c.display_name}'s Bit.</h3>
                    <p>Be the first to buy — every trade will show up here in real time.</p>
                  </div>
                )}
                {activity !== null && activity.length > 0 && (
                  <div className="cp-activity-list">
                    {activity.map((t) => {
                      const arrivedFmt = fmtArrivedUs(t.arrived_at_us);
                      return (
                      <div key={t.trade_id} className="cp-activity-row" data-testid={`cp-activity-${t.trade_id}`}>
                        <div className={`cp-activity-icon ${t.side}`}>
                          <svg viewBox="0 0 16 16" fill="none">
                            {t.side === 'buy'
                              ? <path d="M8 12V4M4 8l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              : <path d="M8 4v8M4 8l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
                          </svg>
                        </div>
                        <div className="cp-activity-text">
                          <b>{t.trader_short}</b> {t.side === 'buy' ? 'bought' : 'sold'} {t.qty} Bit{t.qty === 1 ? '' : 's'} · {t.subtotal.toFixed(2)} pts
                          {arrivedFmt && (
                            <span
                              className="cp-activity-us"
                              data-testid={`cp-activity-us-${t.trade_id}`}
                              title="Arrived at (microsecond precision)"
                            >
                              {arrivedFmt}
                            </span>
                          )}
                        </div>
                        <div className="cp-activity-time">{timeAgo(t.created_at)}</div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}

      {showJoinModal && access && (
        <>
          <div className="ssm-scrim" onClick={() => setShowJoinModal(false)} data-testid="join-modal-scrim" />
          <div className="ssm-modal" role="dialog" aria-modal="true" data-testid="join-clan-modal" style={{ maxWidth: 400 }}>
            <button
              className="ssm-close"
              onClick={() => setShowJoinModal(false)}
              aria-label="Close"
              data-testid="join-modal-close"
            >
              <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div className="ssm-header">
              <div className="ssm-creator-name">Join {c.display_name}'s Clan</div>
              <div className="ssm-creator-handle">@{c.handle}</div>
            </div>
            <div className="ssm-hero profit" data-testid="join-modal-hero">
              <div className="ssm-hero-label">Bits needed to join</div>
              <div className="ssm-hero-value" style={{ color: '#00B4FF' }}>
                {Math.max(0, (access.min_bits_to_join || 0) - (access.bits_owned || 0))}
              </div>
            </div>
            <div className="ssm-rows">
              <div className="ssm-row">
                <span className="ssm-row-label">You currently own</span>
                <span className="ssm-row-value" data-testid="join-modal-owned">{access.bits_owned || 0} Bits</span>
              </div>
              <div className="ssm-row">
                <span className="ssm-row-label">Threshold to unlock Clan</span>
                <span className="ssm-row-value" data-testid="join-modal-threshold">{access.min_bits_to_join} Bits</span>
              </div>
            </div>
            <button
              className="ssm-primary-close"
              onClick={() => { setShowJoinModal(false); navigate(`/trade/${c.handle}`, { state: { mode: 'buy' } }); }}
              data-testid="join-modal-buy"
            >
              Buy Bits →
            </button>
          </div>
        </>
      )}
    </AppShell>
  );
}


/**
 * Bubble chart of trades over the selected timeframe.
 *   X axis = time (left = oldest, right = newest)
 *   Y axis = price after the trade
 *   Radius = qty (bits traded, sqrt-scaled so a 100-Bit trade isn't 100× a 1-Bit one)
 *   Color  = side ('buy' = green, 'sell' = red)
 *
 * Renders a placeholder message when there are no trades.
 */
function BubbleChart({ points, up = true }) {
  if (points === null) {
    return <div className="cp-chart-empty" data-testid="cp-chart-loading">Loading trades…</div>;
  }
  if (!Array.isArray(points) || points.length === 0) {
    return <div className="cp-chart-empty" data-testid="cp-chart-empty">No trades yet in this range.</div>;
  }

  const W = 400, H = 130, padX = 20, padY = 14;
  const usableW = W - padX * 2;
  const usableH = H - padY * 2;

  const times = points.map((p) => (p.t ? new Date(p.t).getTime() : 0));
  const tMin = Math.min(...times);
  const tMax = Math.max(...times);
  const tSpan = tMax - tMin || 1;

  const prices = points.map((p) => Number(p.price || 0));
  const pMin = Math.min(...prices);
  const pMax = Math.max(...prices);
  const pSpan = (pMax - pMin) || Math.max(pMax, 1);

  const qtys = points.map((p) => Math.max(1, Number(p.qty || 1)));
  const qMax = Math.max(...qtys);
  const rMin = 3, rMax = 14;

  return (
    <svg
      className="cp-bubble-svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Trade bubble chart"
      data-testid="cp-bubble-chart"
    >
      {/* Guide line at latest price for context */}
      <line
        x1={0} y1={H - padY}
        x2={W} y2={H - padY}
        stroke="rgba(0, 180, 255,0.14)"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      {points.map((p, i) => {
        const t = new Date(p.t || 0).getTime();
        const x = padX + (usableW * ((t - tMin) / tSpan));
        const y = padY + usableH - (usableH * ((Number(p.price || 0) - pMin) / pSpan));
        const qty = Math.max(1, Number(p.qty || 1));
        // sqrt scaling keeps outliers readable
        const r = rMin + (rMax - rMin) * Math.sqrt(qty / qMax);
        const isBuy = (p.side || 'buy').toLowerCase() === 'buy';
        const color = isBuy ? '#B4FF00' : '#FF5722';
        return (
          <g key={i} data-testid={`cp-bubble-${i}`}>
            <circle
              cx={x} cy={y} r={r}
              fill={color}
              fillOpacity="0.55"
              stroke={color}
              strokeWidth="1.5"
              data-side={isBuy ? 'buy' : 'sell'}
              data-qty={qty}
            >
              <title>{`${isBuy ? '🟢 Buy' : '🔴 Sell'} · ${qty} Bit${qty === 1 ? '' : 's'} @ ${Number(p.price || 0).toFixed(2)} pts · ${new Date(p.t).toLocaleString()}`}</title>
            </circle>
          </g>
        );
      })}
    </svg>
  );
}
